import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Info, ShieldCheck } from 'lucide-react';
import {
  credentialSetFor,
  LINKEDIN_API_TYPE_OF_SET,
  linkedinApiTypeOf,
  socialKeysNoticeName,
  SocialPlatform,
  type LinkedInApiType,
  type SocialConnection,
} from '@mg/shared';
import { api, isSocialAppKeysNotSet } from '../../lib/api';
import { cn } from '../../lib/cn';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { TOUCH_TARGET, TOUCH_TARGET_SQUARE } from '../../components/ui/touchTarget';
import { useFieldId } from '../../components/ui/useFieldId';
import { PLATFORM_META } from './platforms';
import { NotSetUpNotice } from './NotSetUpNotice';
import { reconnectSetOf, SOCIAL_APP_KEYS_STATUS_KEY } from './socialAppKeys';
import type { SocialAppKeysGate } from './useSocialAppKeysStatus';
import {
  CONNECT_COPY,
  FACEBOOK_CREATE_PAGE_URL,
  INSTAGRAM_BEFORE_YOU_CONNECT,
  LINKEDIN_API_TYPES,
  LINKEDIN_CONNECT_COPY,
  LINKEDIN_OPTIONS,
  LINKEDIN_SELECTOR_TITLE,
  START_FAILED,
  type ConnectCopy,
  type ConnectPermission,
} from './connectCopy';

/** What the connect dialog is opened for. */
export interface ConnectTarget {
  platform: SocialPlatform;
  /** The connection a Reconnect re-runs the sign-in for; null for a new connection. */
  reconnect: SocialConnection | null;
  /** The control that opened the dialog: focus goes back to it when the dialog closes. */
  opener: HTMLElement | null;
}

/**
 * The one "Connect your <platform>" dialog, for a new connection and for a Reconnect. It only explains
 * what happens next: Continue asks the server to start the sign-in, and the server signs in with the
 * install's Site Integrations app keys for the connection's credential set, then hands back the
 * platform's URL to send the whole browser to. Nothing typed, nothing stored here.
 *
 * - The page mounts it only while it is open (a fresh mount per open), so the LinkedIn choice, the
 *   start and its error never outlive one opening.
 * - `gate` is the page's own key status: opening the dialog asks the server nothing.
 * - LinkedIn has a selector for the connection type, which picks the credential set. A Reconnect is
 *   locked to the account's own type: the server reconnects it with that type's app, whatever is chosen.
 * - Continue is disabled while the set it needs has no keys, with the not-set-up notice above the
 *   footer saying so (only after a refetch for a platform the page let open, e.g. after a 409).
 * - A start that fails, for any reason, says so under the buttons in fixed words and keeps the dialog
 *   open. A 409 "no app keys" also asks the key status again, so the page catches up.
 */
export function ConnectModal({
  target,
  gate,
  onClose,
}: {
  target: ConnectTarget;
  gate: SocialAppKeysGate;
  onClose: () => void;
}) {
  const { platform, reconnect } = target;
  const queryClient = useQueryClient();
  const isLinkedIn = platform === SocialPlatform.LINKEDIN;
  // A Reconnect keeps the account's connection type; a new connection starts on Community Management.
  const locked = isLinkedIn && reconnect ? lockedApiTypeOf(reconnect) : null;
  const [chosen, setChosen] = useState<LinkedInApiType>('community');
  const apiType = locked ?? chosen;
  const linkedin = isLinkedIn ? apiType : null;
  const copy: ConnectCopy | undefined = isLinkedIn ? LINKEDIN_CONNECT_COPY[apiType] : CONNECT_COPY[platform];

  // The credential set Continue signs in with, and whether it has keys.
  const activeSet = reconnect ? reconnectSetOf(reconnect) : credentialSetFor(platform, apiType);
  const state = gate.stateOf(activeSet);

  // Ids for the notice sentences Continue can point at: one per LinkedIn type, one for everything else.
  const communityNoticeId = useFieldId('social-keys-notice');
  const shareNoticeId = useFieldId('social-keys-notice');
  const noticeId = useFieldId('social-keys-notice');
  let notice: ReactNode = null;
  let noticeSentenceId: string | undefined;
  if (isLinkedIn && !reconnect) {
    // LinkedIn opens when either type has keys. Both types' notices share one cell, so switching type
    // never changes the slot's height.
    const community = gate.stateOf('LINKEDIN_COMMUNITY');
    const share = gate.stateOf('LINKEDIN_SHARE');
    if (community !== 'checking' && share !== 'checking' && (community === 'missing' || share === 'missing')) {
      notice = (
        <Swap
          as="div"
          active={apiType}
          variants={{
            community:
              community === 'missing' ? (
                <NotSetUpNotice
                  id={communityNoticeId}
                  name={socialKeysNoticeName('LINKEDIN_COMMUNITY', platform)}
                  touch
                />
              ) : null,
            standard:
              share === 'missing' ? (
                <NotSetUpNotice id={shareNoticeId} name={socialKeysNoticeName('LINKEDIN_SHARE', platform)} touch />
              ) : null,
          }}
        />
      );
    }
    noticeSentenceId = apiType === 'community' ? communityNoticeId : shareNoticeId;
  } else if (activeSet !== null && state === 'missing') {
    notice = <NotSetUpNotice id={noticeId} name={socialKeysNoticeName(activeSet, platform)} touch />;
    noticeSentenceId = noticeId;
  }

  // Set true by the mount effect itself, not by the ref's first value: development StrictMode mounts,
  // cleans up and mounts again, and a ref that started true would stay false after that cleanup, so
  // every Continue would quietly do nothing. Closing clears it, so a start answered after the dialog
  // is gone never sends the browser away.
  const alive = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const close = () => {
    alive.current = false;
    onClose();
  };

  const start = useMutation({
    mutationFn: async () => {
      if (reconnect) {
        // A Reconnect sends no body: the server works out the type and features from the connection.
        if (!reconnect.id) throw new Error('This connection cannot be reconnected.');
        return api.reconnectSocial(reconnect.id);
      }
      return startFor(platform, apiType);
    },
    onSuccess: ({ url }) => {
      if (alive.current) window.location.href = url;
    },
    onError: (err) => {
      if (isSocialAppKeysNotSet(err)) void queryClient.invalidateQueries({ queryKey: SOCIAL_APP_KEYS_STATUS_KEY });
    },
  });

  if (!copy) return null;
  const meta = PLATFORM_META[platform];

  return (
    <Modal
      open
      onClose={close}
      title={copy.title}
      trapFocus
      returnFocusTo={target.opener}
      closeButtonClassName={TOUCH_TARGET_SQUARE}
      bodyClassName="min-h-0 flex-1 overflow-y-auto px-5 py-4"
      footer={
        <>
          {/* Stays enabled while the start is out: closing now keeps the browser here. */}
          <Button variant="secondary" className={TOUCH_TARGET} onClick={close}>
            Cancel
          </Button>
          <Button
            className={TOUCH_TARGET}
            loading={start.isPending}
            disabled={state !== 'ready'}
            aria-describedby={state === 'missing' ? noticeSentenceId : undefined}
            onClick={() => start.mutate()}
          >
            {copy.continueLabel}
          </Button>
        </>
      }
      aboveFooter={notice}
      belowFooter={
        // Mounted before any error, so the error is announced when it appears.
        <div role="alert">
          {start.isError && (
            <p className="px-5 pb-4 text-right text-sm text-red-600 dark:text-error-dark">{START_FAILED}</p>
          )}
        </div>
      }
    >
      <div className="space-y-5">
        <div className="flex items-start gap-3">
          <div
            aria-hidden="true"
            className={cn('flex h-12 w-12 shrink-0 items-center justify-center rounded-xl', meta.tile)}
          >
            <meta.Icon className="h-6 w-6" />
          </div>
          <p className="text-sm text-ink-label">
            <Region linkedin={linkedin} copy={copy} as="span" pick={(c) => c.intro} />
          </p>
        </div>

        {platform === SocialPlatform.INSTAGRAM && (
          <div className="flex gap-3 rounded-xl border border-line bg-tint/70 px-4 py-3">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-tint-fg" aria-hidden />
            <div className="text-sm">
              <p className="font-semibold text-ink">{INSTAGRAM_BEFORE_YOU_CONNECT.title}</p>
              <p className="mt-1 text-ink-strong">{INSTAGRAM_BEFORE_YOU_CONNECT.body}</p>
            </div>
          </div>
        )}

        {linkedin !== null && <LinkedInSelector value={linkedin} locked={locked !== null} onChange={setChosen} />}

        <Steps linkedin={linkedin} copy={copy} />

        <Permissions linkedin={linkedin} copy={copy} />

        {copy.footnote !== null && (
          <p className="rounded-lg bg-tint/70 px-3 py-2 text-xs text-ink-strong">
            <Region linkedin={linkedin} copy={copy} as="span" pick={(c) => c.footnote} />
          </p>
        )}

        {platform === SocialPlatform.FACEBOOK && (
          <p className="text-sm text-ink-muted">
            Don&apos;t have a Facebook Page yet?{' '}
            <a
              href={FACEBOOK_CREATE_PAGE_URL}
              target="_blank"
              rel="noreferrer"
              className={`inline-flex items-center gap-1 font-medium text-link hover:text-action-hover ${TOUCH_TARGET}`}
            >
              Create one <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </a>
          </p>
        )}
      </div>
    </Modal>
  );
}

/**
 * The start Continue asks for on each platform. Instagram signs in through Facebook Login, never through
 * the generic start (no new Instagram Login connection can start).
 */
function startFor(platform: SocialPlatform, apiType: LinkedInApiType): Promise<{ url: string }> {
  switch (platform) {
    case SocialPlatform.FACEBOOK:
      return api.startFacebookConnect();
    case SocialPlatform.INSTAGRAM:
      return api.startInstagramConnect();
    case SocialPlatform.LINKEDIN:
      return api.startLinkedInConnect(apiType);
    case SocialPlatform.THREADS:
    case SocialPlatform.X:
      return api.startOAuthConnect(platform);
    default:
      return Promise.reject(new Error(`${platform} has no connect flow.`));
  }
}

/** The connection type a LinkedIn Reconnect is locked to: the one its credential set serves. */
function lockedApiTypeOf(conn: SocialConnection): LinkedInApiType {
  const set = reconnectSetOf(conn);
  return set === 'LINKEDIN_COMMUNITY' || set === 'LINKEDIN_SHARE'
    ? LINKEDIN_API_TYPE_OF_SET[set]
    : linkedinApiTypeOf(conn.scopes);
}

/**
 * Both LinkedIn connection types' versions of one region, in ONE grid cell. The inactive one is invisible
 * and hidden from assistive tech but keeps its room, so switching types never changes the dialog's height
 * (it is vertically centred: any change would move it). `visibility: hidden` also takes a hidden link out
 * of the tab order. `as`: 'span' inside phrasing content (`<p>`, `<li>`), 'div' anywhere else.
 */
function Swap({
  active,
  variants,
  as: Tag,
}: {
  active: LinkedInApiType;
  variants: Readonly<Record<LinkedInApiType, ReactNode>>;
  as: 'span' | 'div';
}) {
  return (
    <Tag className="grid">
      {LINKEDIN_API_TYPES.map((type) => (
        <Tag
          key={type}
          className={cn('col-start-1 row-start-1', type === active ? undefined : 'invisible')}
          aria-hidden={type === active ? undefined : true}
        >
          {variants[type]}
        </Tag>
      ))}
    </Tag>
  );
}

/**
 * One region of the copy that differs by LinkedIn connection type: for LinkedIn both versions (Swap); for
 * any other platform its one version.
 */
function Region({
  linkedin,
  copy,
  as,
  pick,
}: {
  linkedin: LinkedInApiType | null;
  copy: ConnectCopy;
  as: 'span' | 'div';
  pick: (copy: ConnectCopy) => ReactNode;
}) {
  if (linkedin === null) return <>{pick(copy)}</>;
  return (
    <Swap
      as={as}
      active={linkedin}
      variants={{ community: pick(LINKEDIN_CONNECT_COPY.community), standard: pick(LINKEDIN_CONNECT_COPY.standard) }}
    />
  );
}

/** "Which LinkedIn connection do you want?": two pressed-or-not buttons, and the helper for the chosen one. */
function LinkedInSelector({
  value,
  locked,
  onChange,
}: {
  value: LinkedInApiType;
  /** A Reconnect: only the account's own type can be chosen; the other is disabled. */
  locked: boolean;
  onChange: (type: LinkedInApiType) => void;
}) {
  const headingId = useFieldId('linkedin-connection-type');
  return (
    <div className="rounded-xl border border-line bg-card px-4 py-4">
      <h3 id={headingId} className="text-sm font-semibold text-ink">
        {LINKEDIN_SELECTOR_TITLE}
      </h3>
      <div role="group" aria-labelledby={headingId} className="mt-2.5 grid grid-cols-2 gap-2">
        {LINKEDIN_API_TYPES.map((type) => {
          const selected = type === value;
          return (
            <button
              key={type}
              type="button"
              aria-pressed={selected}
              disabled={locked && !selected}
              onClick={() => onChange(type)}
              // border-2 in both states, so selecting never changes the size.
              className={cn(
                'focus-ring flex flex-col items-start rounded-lg border-2 px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                TOUCH_TARGET,
                selected ? 'border-action bg-tint' : 'border-line bg-card enabled:hover:bg-hover',
              )}
            >
              <span className={cn('text-sm font-semibold', selected ? 'text-tint-fg' : 'text-ink')}>
                {LINKEDIN_OPTIONS[type].title}
              </span>
              <span className="text-xs text-ink-muted">{LINKEDIN_OPTIONS[type].subtitle}</span>
            </button>
          );
        })}
      </div>
      <p className="mt-2.5 text-xs text-ink-muted">
        <Swap
          as="span"
          active={value}
          variants={{ community: LINKEDIN_CONNECT_COPY.community.helper, standard: LINKEDIN_CONNECT_COPY.standard.helper }}
        />
      </p>
    </div>
  );
}

/** "What happens next": three numbered steps; for LinkedIn the third follows the connection type. */
function Steps({ linkedin, copy }: { linkedin: LinkedInApiType | null; copy: ConnectCopy }) {
  const steps: ReactNode[] = [
    copy.steps[0],
    copy.steps[1],
    <Region linkedin={linkedin} copy={copy} as="span" pick={(c) => c.steps[2]} />,
  ];
  return (
    <section>
      <h3 className="mb-2 text-sm font-semibold text-ink">What happens next</h3>
      <ol className="space-y-2">
        {steps.map((text, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-ink-strong">
            <span
              aria-hidden="true"
              className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-tint text-xs font-bold text-tint-fg"
            >
              {i + 1}
            </span>
            <span>{text}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** A permission's icon: decorative, beside its words. */
const PERMISSION_ICON = 'mt-0.5 h-4 w-4 shrink-0 text-ink-faint';

/** One permission, icon and words, as one line. */
function PermissionLine({ permission: { Icon, text } }: { permission: ConnectPermission }) {
  return (
    <span className="flex items-start gap-2">
      <Icon className={PERMISSION_ICON} aria-hidden />
      {text}
    </span>
  );
}

/** "What we'll be able to do"; for LinkedIn the first two lines follow the connection type. */
function Permissions({ linkedin, copy }: { linkedin: LinkedInApiType | null; copy: ConnectCopy }) {
  const [, second, third] = copy.permissions;
  return (
    <div className="rounded-xl border border-line-soft bg-page/60 px-4 py-3">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
        <ShieldCheck className="h-4 w-4 text-action" aria-hidden /> What we&apos;ll be able to do
      </h3>
      <ul className="mt-2 space-y-1.5 text-sm text-ink-label">
        {/* The first line's icon follows the type too: a Company Page, or the member's own profile. */}
        <li>
          <Region
            linkedin={linkedin}
            copy={copy}
            as="span"
            pick={(c) => <PermissionLine permission={c.permissions[0]} />}
          />
        </li>
        <li className="flex items-start gap-2">
          <second.Icon className={PERMISSION_ICON} aria-hidden />
          <Region linkedin={linkedin} copy={copy} as="span" pick={(c) => c.permissions[1].text} />
        </li>
        <li>
          <PermissionLine permission={third} />
        </li>
      </ul>
      {copy.permissionsNote !== null && <p className="mt-2 text-xs text-ink-muted">{copy.permissionsNote}</p>}
    </div>
  );
}
